import React, { createContext, useState, useContext, ReactNode } from 'react';
import { CancerType, Verbosity, Reasoning } from '../schema';

export enum CaseInputType {
  TEXTAREA = 1,
  FORM = 2,
  FILE = 3,
}

// Interface for file upload
export interface UploadedFile {
  file: File;
  preview?: string;
}

interface CaseIntakeContextType {
  caseContextText: string;
  caseContextJson: string;
  caseInputType: CaseInputType;
  contextCancerType: CancerType;
  caseContextUploadedFiles?: UploadedFile[];
  contextVerbosity: Verbosity;
  contextReasoning: Reasoning;

  setCaseContextText: (text: string) => void;
  setCaseContextJson: (text: string) => void;
  setCaseInputType: (text: CaseInputType) => void;
  setContextCancerType: (cancerType: CancerType) => void;
  setCaseContextUploadedFiles: (files: UploadedFile[]) => void;
  setContextVerbosity: (verbosity: Verbosity) => void;
  setContextReasoning: (reasoning: Reasoning) => void;
  clearCaseContext: () => void;
}

const CaseIntakeContext = createContext<CaseIntakeContextType | undefined>(undefined);

export const CaseIntakeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [caseContextText, setCaseContextText] = useState('');
  const [caseContextJson, setCaseContextJson] = useState('');
  const [caseInputType, setCaseInputType] = useState<CaseInputType>(CaseInputType.TEXTAREA);
  const [contextCancerType, setContextCancerType] = useState<CancerType>(CancerType.NOT_PROVIDED);
  const [caseContextUploadedFiles, setCaseContextUploadedFiles] = useState<UploadedFile[]>([]);
  const [contextVerbosity, setContextVerbosity] = useState<Verbosity>(Verbosity.VERBOSITY_LOW);
  const [contextReasoning, setContextReasoning] = useState<Reasoning>(Reasoning.REASONING_LOW);

  const clearCaseContext = React.useCallback(() => {
    setCaseContextText('');
    setCaseContextJson('');
    setCaseInputType(CaseInputType.TEXTAREA);
    setContextCancerType(CancerType.NOT_PROVIDED);
    setCaseContextUploadedFiles([]);
    setContextVerbosity(Verbosity.VERBOSITY_LOW);
    setContextReasoning(Reasoning.REASONING_LOW);
    sessionStorage.removeItem('lastSubmittedCaseResponse');
  }, []);

  return (
    <CaseIntakeContext.Provider value={{ caseContextText, caseContextJson, caseInputType, contextCancerType, setCaseContextText, setCaseContextJson, setCaseInputType, setContextCancerType, clearCaseContext, caseContextUploadedFiles, setCaseContextUploadedFiles, contextVerbosity, contextReasoning, setContextVerbosity, setContextReasoning }}>
      {children}
    </CaseIntakeContext.Provider>
  );
};

export const useCaseIntake = () => {
  const context = useContext(CaseIntakeContext);
  if (context === undefined) {
    throw new Error('useCaseIntake must be used within a CaseIntakeProvider');
  }
  return context;
};